import { TermSheetFields } from '../../components/TermSheetFields';
import { DocumentUploadSection } from '../../components/DocumentUpload';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { sellersAPI } from '../../services/api';
import { supabase } from '../../lib/supabase';
import { DealComparables } from '../../components/DealComparables';
import { DealReadiness, DealReadinessHint } from '../../components/DealReadiness';
import { DescriptionHelper } from '../../components/DescriptionHelper';
import {
  Building2,
  Globe,
  MapPin,
  Calendar,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  FlaskConical,
  DollarSign,
  ChevronDown,
  Target,
  Sparkles,
  CheckCircle,
  AlertCircle,
  FileText,
} from 'lucide-react';

// Standard options for matching
const THERAPEUTIC_AREAS = [
  { value: 'oncology', label: 'Oncology' },
  { value: 'hematology', label: 'Hematology' },
  { value: 'immunology', label: 'Immunology' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'rare_disease', label: 'Rare Disease' },
  { value: 'metabolic', label: 'Metabolic' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'infectious', label: 'Infectious Disease' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'ophthalmology', label: 'Ophthalmology' },
  { value: 'gi', label: 'Gastrointestinal' },
  { value: 'hepatitis', label: 'Hepatitis/Liver' },
  { value: 'vaccines', label: 'Vaccines' },
  { value: 'pain', label: 'Pain' },
];

const TECH_PLATFORMS = [
  { value: 'small_molecule', label: 'Small Molecule' },
  { value: 'antibody', label: 'Antibody' },
  { value: 'adc', label: 'ADC' },
  { value: 'bispecific', label: 'Bispecific' },
  { value: 'car_t', label: 'CAR-T' },
  { value: 'cell_therapy', label: 'Cell Therapy' },
  { value: 'gene_therapy', label: 'Gene Therapy' },
  { value: 'mrna', label: 'mRNA' },
  { value: 'rnai', label: 'RNAi/siRNA' },
  { value: 'protein_degradation', label: 'Protein Degradation' },
  { value: 'peptide', label: 'Peptide' },
];

const LEAD_STAGES = [
  { value: 'preclinical', label: 'Preclinical' },
  { value: 'phase1', label: 'Phase 1' },
  { value: 'phase2', label: 'Phase 2' },
  { value: 'phase3', label: 'Phase 3' },
  { value: 'approved', label: 'Approved' },
];

const COMPANY_STAGES = [
  { value: 'Discovery', label: 'Discovery' },
  { value: 'Preclinical', label: 'Preclinical' },
  { value: 'Clinical', label: 'Clinical' },
  { value: 'Commercial', label: 'Commercial' },
];

// Countries for matching
const COUNTRIES = [
  { value: 'CN', label: 'China' },
  { value: 'US', label: 'United States' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'DE', label: 'Germany' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'IN', label: 'India' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'DK', label: 'Denmark' },
  { value: 'SE', label: 'Sweden' },
  { value: 'IL', label: 'Israel' },
  { value: 'BE', label: 'Belgium' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
];

// Funding stages for matching
const FUNDING_STAGES = [
  { value: 'seed', label: 'Seed / Angel' },
  { value: 'series_a', label: 'Series A ($10-30M)' },
  { value: 'series_b', label: 'Series B ($30-80M)' },
  { value: 'series_c', label: 'Series C ($80-200M)' },
  { value: 'series_d_plus', label: 'Series D+ ($200M+)' },
  { value: 'pre_ipo', label: 'Pre-IPO' },
  { value: 'public', label: 'Public Company' },
];

const DEAL_PREFERENCES = [
  { value: 'license_out', label: 'License Out (ex-China/ex-Asia)' },
  { value: 'license_global', label: 'Global License' },
  { value: 'co_development', label: 'Co-Development Partnership' },
  { value: 'acquisition', label: 'Acquisition / M&A' },
  { value: 'joint_venture', label: 'Joint Venture' },
  { value: 'option_deal', label: 'Option Deal' },
];

// Founded year ranges for display
const FOUNDED_RANGES = [
  { value: '2020-present', label: '2020 - Present (Startup)', years: [2020, 2025] },
  { value: '2015-2019', label: '2015 - 2019 (Growth)', years: [2015, 2019] },
  { value: '2010-2014', label: '2010 - 2014 (Established)', years: [2010, 2014] },
  { value: '2005-2009', label: '2005 - 2009 (Mature)', years: [2005, 2009] },
  { value: 'before-2005', label: 'Before 2005 (Legacy)', years: [1900, 2004] },
];

// Multi-select component
function MultiSelect({ options, selected, onChange, label, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative">
      <label className="label">{label}</label>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="input cursor-pointer flex items-center justify-between min-h-[42px]"
      >
        <div className="flex flex-wrap gap-1">
          {selected.length > 0 ? (
            selected.map(value => {
              const option = options.find(o => o.value === value);
              return (
                <span
                  key={value}
                  className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                >
                  {option?.label || value}
                </span>
              );
            })
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map(option => (
            <div
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-50 ${
                selected.includes(option.value) ? 'bg-purple-50' : ''
              }`}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                selected.includes(option.value)
                  ? 'bg-purple-600 border-purple-600'
                  : 'border-gray-300'
              }`}>
                {selected.includes(option.value) && (
                  <CheckCircle className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-sm">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CompanyProfile() {
  const { user, refreshProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [assets, setAssets] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [selectedComparableAssetIndex, setSelectedComparableAssetIndex] = useState(0);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'documents'

  // Check for edit mode, tab, and actions from URL params
  useEffect(() => {
    const editParam = searchParams.get('edit');
    const actionParam = searchParams.get('action');
    const scrollToParam = searchParams.get('scrollTo');
    const tabParam = searchParams.get('tab');

    // Handle tab parameter
    if (tabParam === 'documents') {
      setActiveTab('documents');
      // Scroll to document if scrollTo is specified
      if (scrollToParam) {
        setTimeout(() => {
          const element = document.getElementById(`doc-${scrollToParam}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
            }, 3000);
          }
        }, 500);
      }
      // Clear tab and scrollTo params
      searchParams.delete('tab');
      searchParams.delete('scrollTo');
      setSearchParams(searchParams, { replace: true });
      return; // Skip other param handling for tab=documents
    }

    if (editParam === 'true' || editParam === '1') {
      setEditMode(true);
      // If scrollTo is specified, scroll to that field after a short delay
      if (scrollToParam) {
        setTimeout(() => {
          const element = document.getElementById(`field-${scrollToParam}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
            }, 3000);
          }
        }, 500);
      }
    }

    // Handle action=edit_asset - open first incomplete asset for editing
    if (actionParam === 'edit_asset' && assets.length > 0) {
      // Find first asset that needs the specified task
      const taskParam = searchParams.get('task');
      let assetToEdit = assets[0]; // Default to first asset

      if (taskParam) {
        // Find asset missing this field
        const incompleteAsset = assets.find(a => {
          if (taskParam === 'asset_stage') return !a.stage;
          if (taskParam === 'asset_indication') return !a.indication;
          if (taskParam === 'differentiation') return !a.differentiation || a.differentiation.length < 20;
          if (taskParam === 'clinical_data') return !a.clinical_data || a.clinical_data.length < 20;
          if (taskParam === 'deal_preferences') return !a.deal_preferences || a.deal_preferences.length === 0;
          if (taskParam === 'term_sheet_expectations') return !a.term_sheet_expectations || a.term_sheet_expectations.length < 10;
          return false;
        });
        if (incompleteAsset) assetToEdit = incompleteAsset;
      }

      setEditingAsset(assetToEdit);
      setShowAssetForm(true);
    }

    // Clear params after reading
    if (editParam || actionParam) {
      searchParams.delete('edit');
      searchParams.delete('action');
      searchParams.delete('task');
      searchParams.delete('scrollTo');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, assets]);

  const [formData, setFormData] = useState({
    company_name: '',
    company_name_local: '',  // Changed from company_name_cn to support any language
    headquarters: '',
    country: '',             // New: country code for matching
    founded: '',
    stage: '',
    description: '',
    website: '',
    employees: '',
    funding_total: '',
    funding_stage: '',       // New: standardized funding stage for matching
    stock_ticker: '',
    therapeutic_areas: [],
    tech_platforms: [],
    lead_asset_stage: '',
  });

  useEffect(() => {
    if (user?.sellerId) {
      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [user?.sellerId]); // Only depend on sellerId, not the entire user object

  const loadCompanyData = async () => {
    try {
      const response = await sellersAPI.getById(user.sellerId);
      const data = response.data?.data;

      setCompany(data);
      setAssets(data?.assets || []);
      setFormData({
        company_name: data?.company_name || '',
        company_name_local: data?.company_name_local || data?.company_name_cn || '',
        headquarters: data?.headquarters || '',
        country: data?.country || '',
        founded: data?.founded || '',
        stage: data?.stage || '',
        description: data?.description || '',
        website: data?.website || '',
        employees: data?.employees || '',
        funding_total: data?.funding_total || '',
        funding_stage: data?.funding_stage || '',
        stock_ticker: data?.stock_ticker || '',
        therapeutic_areas: data?.therapeutic_areas || [],
        tech_platforms: data?.tech_platforms || [],
        lead_asset_stage: data?.lead_asset_stage || '',
      });
    } catch (error) {
      console.error('Failed to load company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await sellersAPI.update(user.sellerId, formData);
      console.log('[CompanyProfile] Save response:', response.data);

      if (response.data?.success) {
        setEditMode(false);
        await loadCompanyData();

        // Show success notification and scroll to top to display the saved profile
        setSaveSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Hide success notification after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(response.data?.message || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setSaveError(error.response?.data?.message || error.message || 'Failed to save profile. Please try again.');
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!formData.company_name) {
      setCreateError('Company name is required');
      return;
    }

    setSaving(true);
    setCreateError(null);

    try {
      // Use API to create company (handles RLS and user linking)
      const response = await sellersAPI.create({
        company_name: formData.company_name,
        company_name_local: formData.company_name_local || null,
        headquarters: formData.headquarters || null,
        country: formData.country || null,
        founded: formData.founded ? parseInt(formData.founded) : null,
        stage: formData.stage || null,
        description: formData.description || null,
        website: formData.website || null,
        employees: formData.employees ? parseInt(formData.employees) : null,
        funding_total: formData.funding_total || null,
        funding_stage: formData.funding_stage || null,
        stock_ticker: formData.stock_ticker || null,
        therapeutic_areas: formData.therapeutic_areas.length > 0 ? formData.therapeutic_areas : null,
        tech_platforms: formData.tech_platforms.length > 0 ? formData.tech_platforms : null,
        lead_asset_stage: formData.lead_asset_stage || null,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to create company');
      }

      setCreateSuccess(true);

      // Refresh the user profile to get the new sellerId
      if (refreshProfile) {
        await refreshProfile();
      }

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Failed to create company:', error);
      setCreateError(error.response?.data?.message || error.message || 'Failed to create company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsset = async (assetData) => {
    try {
      if (editingAsset) {
        await sellersAPI.updateAsset(user.sellerId, editingAsset.id, assetData);
      } else {
        await sellersAPI.addAsset(user.sellerId, assetData);
      }
      setShowAssetForm(false);
      setEditingAsset(null);
      await loadCompanyData();
    } catch (error) {
      console.error('Failed to save asset:', error);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await sellersAPI.deleteAsset(user.sellerId, assetId);
      await loadCompanyData();
    } catch (error) {
      console.error('Failed to delete asset:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show create company form if no sellerId
  if (!user?.sellerId) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-gray-600">
            Create your company profile to start matching with buyers.
          </p>
        </div>

        {createSuccess ? (
          <div className="card p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Company Created Successfully!
            </h2>
            <p className="text-gray-600 mb-4">
              Your company profile has been created. Matching scores are being calculated...
            </p>
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        ) : (
          <div className="card">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Create Your Company Profile
                  </h2>
                  <p className="text-sm text-gray-600">
                    Fill in your company information to start matching with 47 global pharma buyers
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {createError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700">{createError}</p>
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  Basic Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Company Name (English) *</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="input"
                      placeholder="e.g., BeiGene"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Local Language Name</label>
                    <input
                      type="text"
                      value={formData.company_name_local}
                      onChange={(e) => setFormData({ ...formData, company_name_local: e.target.value })}
                      className="input"
                      placeholder="e.g., 百济神州, ベイジーン"
                    />
                  </div>
                  <div>
                    <label className="label">Country *</label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="input"
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Headquarters City</label>
                    <input
                      type="text"
                      value={formData.headquarters}
                      onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                      className="input"
                      placeholder="e.g., Shanghai, Beijing"
                    />
                  </div>
                  <div>
                    <label className="label">Founded Year</label>
                    <input
                      type="number"
                      value={formData.founded}
                      onChange={(e) => setFormData({ ...formData, founded: e.target.value })}
                      className="input"
                      placeholder="e.g., 2015"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  <div>
                    <label className="label">Company Stage</label>
                    <select
                      value={formData.stage}
                      onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                      className="input"
                    >
                      <option value="">Select stage</option>
                      {COMPANY_STAGES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Funding Stage</label>
                    <select
                      value={formData.funding_stage}
                      onChange={(e) => setFormData({ ...formData, funding_stage: e.target.value })}
                      className="input"
                    >
                      <option value="">Select funding stage</option>
                      {FUNDING_STAGES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="input"
                      placeholder="https://www.company.com"
                    />
                  </div>
                  <div>
                    <label className="label">Employees</label>
                    <input
                      type="number"
                      value={formData.employees}
                      onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                      className="input"
                      placeholder="e.g., 500"
                    />
                  </div>
                  <div>
                    <label className="label">Total Funding Amount</label>
                    <input
                      type="text"
                      value={formData.funding_total}
                      onChange={(e) => setFormData({ ...formData, funding_total: e.target.value })}
                      className="input"
                      placeholder="e.g., $500M (optional)"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <DescriptionHelper
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    companyData={formData}
                  />
                </div>
              </div>

              {/* Matching Criteria - Important! */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-md font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-500" />
                  Matching Criteria
                  <span className="text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                    Important for Smart Matching
                  </span>
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  These fields are used to match you with interested buyers. Fill them in for better matches!
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <MultiSelect
                    label="Therapeutic Areas"
                    placeholder="Select therapeutic areas..."
                    options={THERAPEUTIC_AREAS}
                    selected={formData.therapeutic_areas}
                    onChange={(values) => setFormData({ ...formData, therapeutic_areas: values })}
                  />

                  <MultiSelect
                    label="Technology Platforms"
                    placeholder="Select tech platforms..."
                    options={TECH_PLATFORMS}
                    selected={formData.tech_platforms}
                    onChange={(values) => setFormData({ ...formData, tech_platforms: values })}
                  />

                  <div>
                    <label className="label">Lead Asset Stage</label>
                    <select
                      value={formData.lead_asset_stage}
                      onChange={(e) => setFormData({ ...formData, lead_asset_stage: e.target.value })}
                      className="input"
                    >
                      <option value="">Select most advanced stage</option>
                      {LEAD_STAGES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Stock Ticker (if public)</label>
                    <input
                      type="text"
                      value={formData.stock_ticker}
                      onChange={(e) => setFormData({ ...formData, stock_ticker: e.target.value })}
                      className="input"
                      placeholder="e.g., NASDAQ:BGNE"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <MultiSelect
                      label="Deal Preferences"
                      placeholder="What type of deal are you seeking?"
                      options={DEAL_PREFERENCES}
                      selected={formData.deal_preferences || []}
                      onChange={(values) => setFormData({ ...formData, deal_preferences: values })}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Tip: Being specific helps BD teams prioritize. Most MNCs prefer clear licensing structures
                      (ex-China rights) over vague "open to all options" - it signals deal readiness.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={handleCreateCompany}
                  disabled={saving || !formData.company_name}
                  className="btn-primary flex items-center gap-2 px-6"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Create Company & Start Matching
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Success Notification */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-green-700">Company profile saved successfully!</p>
        </div>
      )}

      {/* Error Notification */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{saveError}</p>
          <button
            onClick={() => setSaveError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-gray-600">
            Keep your company information up to date to attract the right buyers.
          </p>
        </div>
        {!editMode && activeTab === 'profile' && (
          <button
            onClick={() => setEditMode(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'profile'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Profile & Assets
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'documents'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileText className="w-4 h-4" />
          Documents
        </button>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <DocumentUploadSection
          sellerId={user.sellerId}
          documents={company?.documents || {}}
          onUploadComplete={loadCompanyData}
        />
      )}

      {/* Profile Tab Content */}
      {activeTab === 'profile' && (
        <>
      {/* Company Info Card */}
      <div className="card">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            Basic Information
          </h2>
        </div>

        <div className="p-6">
          {editMode ? (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div id="field-company_name">
                  <label className="label">Company Name (English)</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div id="field-company_name_local">
                  <label className="label">Local Language Name</label>
                  <input
                    type="text"
                    value={formData.company_name_local}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name_local: e.target.value })
                    }
                    className="input"
                    placeholder="公司名称 / 会社名 / etc."
                  />
                </div>
                <div id="field-headquarters">
                  <label className="label">Headquarters</label>
                  <input
                    type="text"
                    value={formData.headquarters}
                    onChange={(e) =>
                      setFormData({ ...formData, headquarters: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., Shanghai"
                  />
                </div>
                <div id="field-country">
                  <label className="label">Country</label>
                  <select
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                    className="input"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div id="field-founded">
                  <label className="label">Founded Year</label>
                  <input
                    type="number"
                    value={formData.founded}
                    onChange={(e) =>
                      setFormData({ ...formData, founded: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., 2015"
                  />
                </div>
                <div id="field-stage">
                  <label className="label">Company Stage</label>
                  <select
                    value={formData.stage}
                    onChange={(e) =>
                      setFormData({ ...formData, stage: e.target.value })
                    }
                    className="input"
                  >
                    <option value="">Select stage</option>
                    {COMPANY_STAGES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div id="field-website">
                  <label className="label">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) =>
                      setFormData({ ...formData, website: e.target.value })
                    }
                    className="input"
                    placeholder="https://www.company.com"
                  />
                </div>
                <div>
                  <label className="label">Employees</label>
                  <input
                    type="number"
                    value={formData.employees}
                    onChange={(e) =>
                      setFormData({ ...formData, employees: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., 500"
                  />
                </div>
                <div>
                  <label className="label">Total Funding</label>
                  <input
                    type="text"
                    value={formData.funding_total}
                    onChange={(e) =>
                      setFormData({ ...formData, funding_total: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., $500M"
                  />
                </div>
                <div>
                  <label className="label">Funding Stage</label>
                  <select
                    value={formData.funding_stage}
                    onChange={(e) =>
                      setFormData({ ...formData, funding_stage: e.target.value })
                    }
                    className="input"
                  >
                    <option value="">Select funding stage</option>
                    {FUNDING_STAGES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <DescriptionHelper
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                companyData={formData}
              />

              {/* Matching Criteria Section */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-500" />
                  Matching Criteria
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div id="field-therapeutic_areas">
                    <MultiSelect
                      label="Therapeutic Areas"
                      placeholder="Select therapeutic areas..."
                      options={THERAPEUTIC_AREAS}
                      selected={formData.therapeutic_areas}
                      onChange={(values) => setFormData({ ...formData, therapeutic_areas: values })}
                    />
                  </div>

                  <div id="field-tech_platforms">
                    <MultiSelect
                      label="Technology Platforms"
                      placeholder="Select tech platforms..."
                      options={TECH_PLATFORMS}
                      selected={formData.tech_platforms}
                      onChange={(values) => setFormData({ ...formData, tech_platforms: values })}
                    />
                  </div>

                  <div id="field-lead_asset_stage">
                    <label className="label">Lead Asset Stage</label>
                    <select
                      value={formData.lead_asset_stage}
                      onChange={(e) => setFormData({ ...formData, lead_asset_stage: e.target.value })}
                      className="input"
                    >
                      <option value="">Select most advanced stage</option>
                      {LEAD_STAGES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div id="field-stock_ticker">
                    <label className="label">Stock Ticker</label>
                    <input
                      type="text"
                      value={formData.stock_ticker}
                      onChange={(e) => setFormData({ ...formData, stock_ticker: e.target.value })}
                      className="input"
                      placeholder="e.g., NASDAQ:BGNE"
                    />
                  </div>

                  <div id="field-company_deal_preferences" className="md:col-span-2">
                    <MultiSelect
                      label="Deal Preferences"
                      placeholder="What type of deal are you seeking?"
                      options={DEAL_PREFERENCES}
                      selected={formData.deal_preferences || []}
                      onChange={(values) => setFormData({ ...formData, deal_preferences: values })}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Tip: Being specific helps BD teams prioritize. Most MNCs prefer clear licensing structures
                      (ex-China rights) over vague "open to all options" - it signals deal readiness.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEditMode(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Company Header */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {company?.company_name}
                  </h3>
                  {(company?.company_name_local || company?.company_name_cn) && (
                    <p className="text-gray-600">{company.company_name_local || company.company_name_cn}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {company?.stage || 'Stage not specified'}
                  </p>
                </div>
              </div>

              {/* Company Details Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Location */}
                {(company?.headquarters || company?.country) && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>
                      {company.headquarters}
                      {company.headquarters && company.country && ', '}
                      {company.country && (COUNTRIES.find(c => c.value === company.country)?.label || company.country)}
                    </span>
                  </div>
                )}

                {/* Founded */}
                {company?.founded && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Founded {company.founded}</span>
                  </div>
                )}

                {/* Website */}
                {company?.website && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary-600 hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                  </a>
                )}

                {/* Employees */}
                {company?.employees && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>{company.employees.toLocaleString()} employees</span>
                  </div>
                )}

                {/* Total Funding */}
                {company?.funding_total && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span>Funding: {company.funding_total}</span>
                  </div>
                )}

                {/* Funding Stage */}
                {company?.funding_stage && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span>{FUNDING_STAGES.find(f => f.value === company.funding_stage)?.label || company.funding_stage}</span>
                  </div>
                )}

                {/* Stock Ticker */}
                {company?.stock_ticker && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Target className="w-4 h-4 text-gray-400" />
                    <span>{company.stock_ticker}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {company?.description && (
                <div className="pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">About</h4>
                  <p className="text-gray-600">{company.description}</p>
                </div>
              )}

              {/* Matching Criteria - Always show section */}
              <div className="pt-4 border-t border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-500" />
                  Matching Criteria
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Therapeutic Areas */}
                  <div>
                    <span className="text-xs text-gray-500">Therapeutic Areas:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {company?.therapeutic_areas?.length > 0 ? (
                        company.therapeutic_areas.map(area => (
                          <span key={area} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                            {THERAPEUTIC_AREAS.find(t => t.value === area)?.label || area}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not specified</span>
                      )}
                    </div>
                  </div>

                  {/* Tech Platforms */}
                  <div>
                    <span className="text-xs text-gray-500">Technology Platforms:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {company?.tech_platforms?.length > 0 ? (
                        company.tech_platforms.map(platform => (
                          <span key={platform} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                            {TECH_PLATFORMS.find(t => t.value === platform)?.label || platform}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not specified</span>
                      )}
                    </div>
                  </div>

                  {/* Lead Asset Stage */}
                  <div>
                    <span className="text-xs text-gray-500">Lead Asset Stage:</span>
                    <div className="mt-1">
                      {company?.lead_asset_stage ? (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                          {LEAD_STAGES.find(s => s.value === company.lead_asset_stage)?.label || company.lead_asset_stage}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not specified</span>
                      )}
                    </div>
                  </div>

                  {/* Company Stage */}
                  <div>
                    <span className="text-xs text-gray-500">Company Stage:</span>
                    <div className="mt-1">
                      {company?.stage ? (
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded">
                          {COMPANY_STAGES.find(s => s.value === company.stage)?.label || company.stage}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not specified</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Assets */}
      <div className="card">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Pipeline Assets
          </h2>
          <button
            onClick={() => {
              setEditingAsset(null);
              setShowAssetForm(true);
            }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Asset
          </button>
        </div>

        {assets.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {assets.map((asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                onEdit={() => {
                  setEditingAsset(asset);
                  setShowAssetForm(true);
                }}
                onDelete={() => handleDeleteAsset(asset.id)}
              />
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No pipeline assets added yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Add your drug candidates to attract buyer interest
            </p>
          </div>
        )}
      </div>

      {/* Deal Comparables - Standalone Section */}
      {assets.length > 0 && (
        <div id="deal-comparables" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Deal Comparables</h2>
                  <p className="text-sm text-gray-500">
                    Historical deals matching your asset's profile
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Asset Selector Tabs */}
          {assets.length > 1 && (
            <div className="px-6 pt-4 pb-2 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 mr-2">Select Asset:</span>
                {assets.map((asset, index) => (
                  <button
                    key={asset.id}
                    onClick={() => setSelectedComparableAssetIndex(index)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      selectedComparableAssetIndex === index
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {asset.asset_name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="p-6">
            <DealComparables
              key={assets[selectedComparableAssetIndex]?.id}
              asset={{
                // Primary: asset's specific indication
                therapeutic_area: assets[selectedComparableAssetIndex]?.indication || company?.therapeutic_areas?.[0] || 'Oncology',
                // Fallback: company's broader therapeutic area (for when specific indication has no matches)
                fallback_therapeutic_area: company?.therapeutic_areas?.[0] || 'Oncology',
                development_stage: assets[selectedComparableAssetIndex]?.stage,
                modality: assets[selectedComparableAssetIndex]?.modality,
                asset_name: assets[selectedComparableAssetIndex]?.asset_name
              }}
            />
          </div>
        </div>
      )}

      {/* Deal Readiness */}
      <DealReadiness
        company={company}
        assets={assets}
        maxSuggestions={5}
        onEditMode={(taskId, isAssetTask, isDocumentTask) => {
          // Document tasks - switch to documents tab
          if (isDocumentTask) {
            setActiveTab('documents');
            setTimeout(() => {
              const element = document.getElementById(`doc-${taskId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
                setTimeout(() => {
                  element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
                }, 3000);
              }
            }, 300);
            return;
          }

          if (isAssetTask && assets.length > 0) {
            // Find first asset missing this field
            let assetToEdit = assets[0];
            const incompleteAsset = assets.find(a => {
              if (taskId === 'asset_stage') return !a.stage;
              if (taskId === 'asset_indication') return !a.indication;
              if (taskId === 'differentiation') return !a.differentiation || a.differentiation.length < 20;
              if (taskId === 'clinical_data') return !a.clinical_data || a.clinical_data.length < 20;
              if (taskId === 'deal_preferences') return !a.deal_preferences || a.deal_preferences.length === 0;
              if (taskId === 'term_sheet_expectations') return !a.term_sheet_expectations || a.term_sheet_expectations.length < 10;
              return false;
            });
            if (incompleteAsset) assetToEdit = incompleteAsset;
            setEditingAsset(assetToEdit);
            setShowAssetForm(true);
          } else {
            // Company-level task - enter edit mode and scroll to field
            setEditMode(true);
            setTimeout(() => {
              const element = document.getElementById(`field-${taskId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
                setTimeout(() => {
                  element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
                }, 3000);
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }, 300);
          }
        }}
      />
      </>
      )}

      {/* Asset Form Modal */}
      {showAssetForm && (
        <AssetFormModal
          asset={editingAsset}
          onSave={handleSaveAsset}
          onClose={() => {
            setShowAssetForm(false);
            setEditingAsset(null);
          }}
        />
      )}
    </div>
  );
}

function AssetCard({ asset, onEdit, onDelete }) {
  const stageColors = {
    'Preclinical': 'bg-gray-100 text-gray-700',
    'Phase 1': 'bg-blue-100 text-blue-700',
    'Phase 2': 'bg-yellow-100 text-yellow-700',
    'Phase 3': 'bg-orange-100 text-orange-700',
    'Approved': 'bg-green-100 text-green-700',
  };

  return (
    <div className="p-6 hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-gray-900">{asset.asset_name}</h3>
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${
                stageColors[asset.stage] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {asset.stage}
            </span>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
            {asset.modality && (
              <span>
                <strong>Modality:</strong> {asset.modality}
              </span>
            )}
            {asset.target && (
              <span>
                <strong>Target:</strong> {asset.target}
              </span>
            )}
            {asset.indication && (
              <span>
                <strong>Indication:</strong> {asset.indication}
              </span>
            )}
          </div>

          {asset.differentiation && (
            <p className="text-sm text-gray-600 mb-2">
              <strong>Differentiation:</strong> {asset.differentiation}
            </p>
          )}

          {asset.clinical_data && (
            <p className="text-sm text-gray-500">
              <strong>Clinical Data:</strong> {asset.clinical_data}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetFormModal({ asset, onSave, onClose }) {
  const [formData, setFormData] = useState({
    asset_name: asset?.asset_name || '',
    modality: asset?.modality || '',
    target: asset?.target || '',
    indication: asset?.indication || '',
    stage: asset?.stage || '',
    differentiation: asset?.differentiation || '',
    clinical_data: asset?.clinical_data || '',
    deal_preferences: asset?.deal_preferences || [],
    term_sheet_expectations: asset?.term_sheet_expectations || '',
upfront_range: asset?.upfront_range || '',
    milestone_range: asset?.milestone_range || '',
    royalty_range: asset?.royalty_range || '',
    territory_rights: asset?.territory_rights || [],
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {asset ? 'Edit Asset' : 'Add New Asset'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">Asset Name *</label>
            <input
              type="text"
              value={formData.asset_name}
              onChange={(e) =>
                setFormData({ ...formData, asset_name: e.target.value })
              }
              className="input"
              placeholder="e.g., RC48, Carvykti"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Modality</label>
              <select
                value={formData.modality}
                onChange={(e) =>
                  setFormData({ ...formData, modality: e.target.value })
                }
                className="input"
              >
                <option value="">Select modality</option>
                <option value="ADC">ADC</option>
                <option value="CAR-T">CAR-T</option>
                <option value="Small Molecule">Small Molecule</option>
                <option value="Bispecific">Bispecific</option>
                <option value="mRNA">mRNA</option>
                <option value="Gene Therapy">Gene Therapy</option>
                <option value="Antibody">Antibody</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="label">Development Stage</label>
              <select
                value={formData.stage}
                onChange={(e) =>
                  setFormData({ ...formData, stage: e.target.value })
                }
                className="input"
              >
                <option value="">Select stage</option>
                <option value="Discovery">Discovery</option>
                <option value="Preclinical">Preclinical</option>
                <option value="Phase 1">Phase 1</option>
                <option value="Phase 2">Phase 2</option>
                <option value="Phase 3">Phase 3</option>
                <option value="Approved">Approved</option>
              </select>
            </div>

            <div>
              <label className="label">Target</label>
              <input
                type="text"
                value={formData.target}
                onChange={(e) =>
                  setFormData({ ...formData, target: e.target.value })
                }
                className="input"
                placeholder="e.g., HER2, BCMA, PD-1"
              />
            </div>

            <div>
              <label className="label">Indication</label>
              <input
                type="text"
                value={formData.indication}
                onChange={(e) =>
                  setFormData({ ...formData, indication: e.target.value })
                }
                className="input"
                placeholder="e.g., Breast Cancer, Multiple Myeloma"
              />
            </div>
          </div>

          <div>
            <label className="label">Differentiation</label>
            <textarea
              value={formData.differentiation}
              onChange={(e) =>
                setFormData({ ...formData, differentiation: e.target.value })
              }
              className="input min-h-[80px]"
              placeholder="What makes this asset unique?"
            />
            <p className="text-xs text-gray-500 mt-1 italic">
              Tip: BD teams look for clear competitive advantages. Include: mechanism novelty,
              best-in-class data, IP strength, or manufacturing advantages vs competitors.
            </p>
          </div>

          <div>
            <label className="label">Clinical Data Summary</label>
            <textarea
              value={formData.clinical_data}
              onChange={(e) =>
                setFormData({ ...formData, clinical_data: e.target.value })
              }
              className="input min-h-[80px]"
              placeholder="Key clinical results, ORR, safety profile, etc."
            />
            <p className="text-xs text-gray-500 mt-1 italic">
              Tip: Focus on headline numbers that matter: ORR/CR rates, PFS/OS data,
              safety profile (Grade 3+ AE rates), and comparison to SOC if available.
            </p>
          </div>

          {/* Deal Intent Section */}
          <div className="pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Deal Intent for This Asset</h4>

            <div className="space-y-4">
              <div>
                <label className="label">Deal Preferences</label>
                <div className="flex flex-wrap gap-2">
                  {DEAL_PREFERENCES.map(pref => (
                    <label key={pref.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.deal_preferences || []).includes(pref.value)}
                        onChange={(e) => {
                          const current = formData.deal_preferences || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, deal_preferences: [...current, pref.value] });
                          } else {
                            setFormData({ ...formData, deal_preferences: current.filter(v => v !== pref.value) });
                          }
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{pref.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1 italic">
                  Tip: Being specific signals deal readiness. "License Out ex-China" is clearer than "open to options".
                </p>
              </div>

              <div className="pt-4 border-t border-gray-100">
    <TermSheetFields
      upfrontRange={formData.upfront_range}
      milestoneRange={formData.milestone_range}
      royaltyRange={formData.royalty_range}
      territoryRights={formData.territory_rights}
      termSheetNotes={formData.term_sheet_expectations}
      onChange={({ field, value }) => setFormData({ ...formData, [field]: value })}
      showHelp={true}
    />
  </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : asset ? 'Update Asset' : 'Add Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

